import { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Lock, User, Users, Briefcase, BookOpen, Package, Calculator, Sparkles, X } from 'lucide-react';

export default function RoleSelection() {
  const { login } = useApp();
  const [showLogin, setShowLogin] = useState(false);
  const [showFinansLogin, setShowFinansLogin] = useState(false);
  const [finansSifre, setFinansSifre] = useState('');
  const [finansLoading, setFinansLoading] = useState(false);
  const [kullanicilar, setKullanicilar] = useState([]);
  const [subeler, setSubeler] = useState([]);
  const [selectedKullanici, setSelectedKullanici] = useState(null);
  const [sifre, setSifre] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);

  useEffect(() => {
    if (showLogin) {
      loadData();
    }
  }, [showLogin]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [kullanicilarRes, subelerRes] = await Promise.all([
        api.getKullanicilar(),
        api.getSubeler()
      ]);
      setKullanicilar(kullanicilarRes.data);
      setSubeler(subelerRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!selectedKullanici) {
      toast.error('Lütfen bir kullanıcı seçin');
      return;
    }
    if (!sifre) {
      toast.error('Lütfen şifrenizi girin');
      return;
    }

    setLoginLoading(true);
    try {
      const result = await login(selectedKullanici, sifre);
      toast.success(`Hoş geldiniz, ${result.kullanici.ad}!`);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Şifre hatalı!');
      } else if (error.response?.status === 404) {
        toast.error('Kullanıcı bulunamadı');
      } else {
        toast.error('Giriş yapılırken hata oluştu');
      }
    } finally {
      setLoginLoading(false);
    }
  };

  const handleFinansLogin = () => {
    if (!finansSifre) {
      toast.error('Lütfen şifrenizi girin');
      return;
    }
    
    setFinansLoading(true);
    
    // Şifre kontrolü - "miba2024" veya "finans123"
    if (finansSifre === 'miba2024' || finansSifre === 'finans123') {
      toast.success('Giriş başarılı! Yönlendiriliyorsunuz...');
      setTimeout(() => {
        window.location.href = 'https://finanskontrol.mibatakip.com';
      }, 1000);
    } else {
      toast.error('Şifre hatalı!');
      setFinansLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  const handleFinansKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleFinansLogin();
    }
  };

  const getSubeAd = (subeId) => {
    const sube = subeler.find(s => s.id === subeId);
    return sube?.ad || '';
  };

  // Finans Kontrol şifre dialogu
  if (showFinansLogin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4"
           style={{
             backgroundImage: 'linear-gradient(to right, #E7E5E4 1px, transparent 1px), linear-gradient(to bottom, #E7E5E4 1px, transparent 1px)',
             backgroundSize: '40px 40px'
           }}>
        <div className="w-full max-w-md relative">
          <button
            onClick={() => { setShowFinansLogin(false); setFinansSifre(''); }}
            className="absolute -top-12 left-0 text-sm text-textSecondary hover:text-textPrimary flex items-center gap-1"
          >
            ← Geri Dön
          </button>
          
          <div className="bg-surface border-2 border-border shadow-lg p-6 sm:p-8">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-[#002FA7]/10 flex items-center justify-center">
                <Calculator className="h-10 w-10 text-[#002FA7]" />
              </div>
            </div>

            <div className="text-center mb-8 space-y-1">
              <h1 className="text-2xl sm:text-3xl font-heading font-bold text-textPrimary">
                Finans Kontrol
              </h1>
              <p className="text-sm text-textSecondary">
                Devam etmek için şifrenizi girin
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  <Lock className="inline h-4 w-4 mr-1" />
                  Şifre
                </label>
                <Input
                  type="password"
                  value={finansSifre}
                  onChange={(e) => setFinansSifre(e.target.value)}
                  onKeyPress={handleFinansKeyPress}
                  placeholder="Şifrenizi girin"
                  className="h-12"
                  autoFocus
                  data-testid="finans-password-input"
                />
              </div>

              <Button
                onClick={handleFinansLogin}
                disabled={finansLoading}
                data-testid="finans-login-btn"
                className="w-full h-12 bg-[#002FA7] hover:bg-[#00227A] text-white font-medium tracking-wide uppercase text-sm transition-all active:translate-y-[1px]"
              >
                {finansLoading ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Giriş Yapılıyor...
                  </span>
                ) : (
                  'Giriş Yap'
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Modül seçim ekranı
  if (!showLogin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4"
           style={{
             backgroundImage: 'linear-gradient(to right, #E7E5E4 1px, transparent 1px), linear-gradient(to bottom, #E7E5E4 1px, transparent 1px)',
             backgroundSize: '40px 40px'
           }}>
        <div className="w-full max-w-6xl">
          <div className="text-center mb-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_branch-eod-system/artifacts/oajdv090_IMG_1236.png" 
              alt="MİBA GRUP Logo" 
              className="h-20 w-auto object-contain mx-auto mb-6"
            />
            <h1 className="text-4xl font-heading font-bold text-textPrimary mb-2">
              MİBA GRUP
            </h1>
            <p className="text-lg text-textSecondary">
              Lütfen kullanmak istediğiniz modülü seçin
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {/* Kasa Takip Modülü */}
            <button
              onClick={() => setShowLogin(true)}
              className="bg-surface border-2 border-border hover:border-primary p-8 transition-all hover:shadow-lg group"
            >
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <Briefcase className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <h3 className="text-2xl font-heading font-bold text-textPrimary mb-2">
                    Kasa Takip
                  </h3>
                  <p className="text-sm text-textSecondary">
                    Ödemeler, Çekler, Krediler ve Bankalar
                  </p>
                </div>
              </div>
            </button>

            {/* Stok Sayım Modülü */}
            <a
              href="https://stok.mibatakip.com/giris.html"
              className="bg-surface border-2 border-border hover:border-[#2196f3] p-8 transition-all hover:shadow-lg group block"
            >
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-[#2196f3]/10 flex items-center justify-center group-hover:bg-[#2196f3]/20 transition-colors">
                  <Package className="h-8 w-8 text-[#2196f3]" />
                </div>
                <div>
                  <h3 className="text-2xl font-heading font-bold text-textPrimary mb-2">
                    Stok Sayım
                  </h3>
                  <p className="text-sm text-textSecondary">
                    Depo Sayımı ve Stok Kontrolü
                  </p>
                </div>
                <div className="inline-flex items-center px-3 py-1 bg-[#2196f3]/10 text-[#2196f3] text-xs font-medium rounded-full">
                  YENİ
                </div>
              </div>
            </a>

            {/* Plasiyer Modülü */}
            <a
              href="/plasiyer"
              className="bg-surface border-2 border-border hover:border-[#f97316] p-8 transition-all hover:shadow-lg group block"
            >
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-[#f97316]/10 flex items-center justify-center group-hover:bg-[#f97316]/20 transition-colors">
                  <Users className="h-8 w-8 text-[#f97316]" />
                </div>
                <div>
                  <h3 className="text-2xl font-heading font-bold text-textPrimary mb-2">
                    Plasiyer
                  </h3>
                  <p className="text-sm text-textSecondary">
                    Ziyaret Takibi, Sipariş ve Raporlama
                  </p>
                </div>
                <div className="inline-flex items-center px-3 py-1 bg-[#f97316]/10 text-[#f97316] text-xs font-medium rounded-full">
                  YENİ
                </div>
              </div>
            </a>

            {/* Katalog Modülü */}
            <a
              href="/katalog"
              className="bg-surface border-2 border-border hover:border-[#E60000] p-8 transition-all hover:shadow-lg group block"
            >
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-[#E60000]/10 flex items-center justify-center group-hover:bg-[#E60000]/20 transition-colors">
                  <BookOpen className="h-8 w-8 text-[#E60000]" />
                </div>
                <div>
                  <h3 className="text-2xl font-heading font-bold text-textPrimary mb-2">
                    Katalog
                  </h3>
                  <p className="text-sm text-textSecondary">
                    Ürün Yönetimi ve PDF Katalog
                  </p>
                </div>
                <div className="inline-flex items-center px-3 py-1 bg-[#E60000]/10 text-[#E60000] text-xs font-medium rounded-full">
                  YENİ
                </div>
              </div>
            </a>

            {/* Finans Kontrol Modülü - YENİ */}
            <button
              onClick={() => setShowFinansLogin(true)}
              className="bg-surface border-2 border-border hover:border-[#002FA7] p-8 transition-all hover:shadow-lg group"
            >
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-[#002FA7]/10 flex items-center justify-center group-hover:bg-[#002FA7]/20 transition-colors">
                  <Calculator className="h-8 w-8 text-[#002FA7]" />
                </div>
                <div>
                  <h3 className="text-2xl font-heading font-bold text-textPrimary mb-2">
                    Finans Kontrol
                  </h3>
                  <p className="text-sm text-textSecondary">
                    Gelir, Gider, Cari ve Raporlar
                  </p>
                </div>
                <div className="inline-flex items-center px-3 py-1 bg-[#002FA7]/10 text-[#002FA7] text-xs font-medium rounded-full">
                  YENİ
                </div>
              </div>
            </button>

            {/* Netsis AI Asistan Modülü - YENİ */}
            <a
              href="https://netsis-analytics-ai.preview.emergentagent.com"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-surface border-2 border-border hover:border-[#059669] p-8 transition-all hover:shadow-lg group block"
            >
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-[#059669]/10 flex items-center justify-center group-hover:bg-[#059669]/20 transition-colors">
                  <Sparkles className="h-8 w-8 text-[#059669]" />
                </div>
                <div>
                  <h3 className="text-2xl font-heading font-bold text-textPrimary mb-2">
                    Netsis AI
                  </h3>
                  <p className="text-sm text-textSecondary">
                    Stok, Risk ve Kar Analizi (Yapay Zeka)
                  </p>
                </div>
                <div className="inline-flex items-center px-3 py-1 bg-[#059669]/10 text-[#059669] text-xs font-medium rounded-full">
                  YENİ
                </div>
              </div>
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-textSecondary">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4"
         style={{
           backgroundImage: 'linear-gradient(to right, #E7E5E4 1px, transparent 1px), linear-gradient(to bottom, #E7E5E4 1px, transparent 1px)',
           backgroundSize: '40px 40px'
         }}>
      <div className="w-full max-w-md relative">
        <button
          onClick={() => setShowLogin(false)}
          className="absolute -top-12 left-0 text-sm text-textSecondary hover:text-textPrimary"
        >
          ← Geri Dön
        </button>
        
        <div className="bg-surface border-2 border-border shadow-lg p-6 sm:p-8">
          <div className="flex justify-center mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_branch-eod-system/artifacts/oajdv090_IMG_1236.png" 
              alt="MİBA GRUP Logo" 
              className="h-16 w-auto object-contain"
            />
          </div>

          <div className="text-center mb-8 space-y-1">
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-textPrimary">
              Kasa Takip Sistemi
            </h1>
            <p className="text-sm text-textSecondary">
              Giriş yaparak devam edin
            </p>
          </div>

          <div className="space-y-4" data-testid="login-form">
            <div className="relative z-10">
              <label className="block text-sm font-medium text-textPrimary mb-2">
                <User className="inline h-4 w-4 mr-1" />
                Kullanıcı
              </label>
              <Select onValueChange={setSelectedKullanici} value={selectedKullanici}>
                <SelectTrigger data-testid="user-select" className="w-full h-12 bg-surface">
                  <SelectValue placeholder="Kullanıcı seçin" />
                </SelectTrigger>
                <SelectContent className="z-50 bg-surface max-h-[300px]">
                  {kullanicilar.map((kullanici) => {
                    const getRolLabel = (rol) => {
                      if (rol === 'admin') return 'Yönetici';
                      if (rol === 'sube_sorumlusu') return 'Şube Sorumlusu';
                      if (rol === 'veri_giris') return getSubeAd(kullanici.subeId);
                      return getSubeAd(kullanici.subeId);
                    };
                    
                    return (
                      <SelectItem key={kullanici.id} value={kullanici.id}>
                        <div className="flex flex-col py-1">
                          <span className="font-medium text-sm">{kullanici.ad}</span>
                          <span className="text-xs text-textSecondary">
                            {getRolLabel(kullanici.rol)}
                          </span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                <Lock className="inline h-4 w-4 mr-1" />
                Şifre
              </label>
              <Input
                type="password"
                value={sifre}
                onChange={(e) => setSifre(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Şifrenizi girin"
                className="h-12"
                data-testid="password-input"
              />
            </div>

            <Button
              onClick={handleLogin}
              disabled={loginLoading}
              data-testid="login-btn"
              className="w-full h-12 bg-primary hover:bg-primaryHover text-primaryForeground font-medium tracking-wide uppercase text-sm transition-all active:translate-y-[1px]"
            >
              {loginLoading ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Giriş Yapılıyor...
                </span>
              ) : (
                'Giriş Yap'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
